import type { Direction, Point } from "../types/directionsTypes";

export type HighlightState =
  | { type: "none"; currentFloor: number }
  | {
      type: "full_route";
      floors: Set<number>;
      stairs: Set<string>;
      startRoomId: string | null;
      endRoomId: string | null;
      activePoints: Point[];
    }
  | {
      type: "segment";
      floor: number;
      routeFloors: Set<number>;
      routeStairs: Set<string>;
      stairs: Set<string>;
      rooms: Set<string>;
      endpointRooms: Set<string>;
      activePoints: Point[];
      isCorridorSegment: boolean;
    }
  | {
      type: "floor_selected";
      selectedFloor: number;
      routeFloors: Set<number>;
      routeStairs: Set<string>;
      activePoints: Point[];
    };

const isRoom = (parent: string): boolean => {
  const p = parent.toLowerCase();
  return (
    p.startsWith("room_") ||
    p === "room_library" ||
    p.startsWith("room_wardrobe")
  );
};

const isStairs = (parent: string): boolean => {
  return parent.toLowerCase().startsWith("stairs_");
};

export function computeHighlightState(
  direction: Direction | null,
  selectedSegmentId: number | null,
  currentFloor: number,
  isFloorManuallySelected: boolean,
): HighlightState {
  if (!direction) {
    return { type: "none", currentFloor };
  }

  const routeFloors = new Set(direction.segmentationRoute.selectedFloors);
  const routeStairs = new Set(direction.segmentationRoute.selectedStairs);

  // Режим: выбран этаж вручную (при наличии маршрута)
  if (isFloorManuallySelected) {
    return {
      type: "floor_selected",
      selectedFloor: currentFloor,
      routeFloors,
      routeStairs,
      activePoints: direction.points,
    };
  }

  // Режим: весь маршрут
  if (selectedSegmentId === null) {
    const floors = new Set(direction.segmentationRoute.selectedFloors);
    const stairs = new Set(direction.segmentationRoute.selectedStairs);

    // Находим старт и финиш комнаты
    const startPointName = direction.segmentationRoute.startPointMeshName;
    const endPointName = direction.segmentationRoute.endPointMeshName;

    // Поиск по meshName с fallback на первую/последнюю точку
    let startPoint = direction.points.find(
      (p) => p.meshName === startPointName,
    );
    let endPoint = direction.points.find((p) => p.meshName === endPointName);

    // Fallback: если точка не найдена, использовать первую/последнюю
    if (!startPoint && direction.points.length > 0) {
      startPoint = direction.points[0];
    }
    if (!endPoint && direction.points.length > 0) {
      endPoint = direction.points[direction.points.length - 1];
    }

    const startRoomId =
      startPoint && isRoom(startPoint.parent) ? startPoint.parent : null;
    const endRoomId =
      endPoint && isRoom(endPoint.parent) ? endPoint.parent : null;

    return {
      type: "full_route",
      floors,
      stairs,
      startRoomId,
      endRoomId,
      activePoints: direction.points,
    };
  }

  // Режим: выбран сегмент
  const segment = direction.segmentationRoute.segments.find(
    (s) => s.id === selectedSegmentId,
  );
  if (!segment) {
    return { type: "none", currentFloor };
  }

  // Собираем точки сегмента
  const pointsByName = new Map(direction.points.map((p) => [p.meshName, p]));
  const activePoints = segment.pointMeshNames
    .map((name) => pointsByName.get(name))
    .filter((p): p is Point => p !== undefined);

  const rooms = new Set<string>();
  const endpointRooms = new Set<string>();
  const stairs = new Set<string>();

  // Если сегмент - это вход в комнату или гардероб
  if (
    segment.type === "room_entry" ||
    segment.type === "room" ||
    segment.type === "wardrobe" ||
    segment.type === "wardrobe_entry"
  ) {
    // Находим комнату через parent точек сегмента
    for (const pt of activePoints) {
      if (isRoom(pt.parent)) {
        endpointRooms.add(pt.parent);
      }
    }
  }

  // Если сегмент - лестница, добавляем все лестницы из parentNames
  if (segment.type === "stairs") {
    for (const parentName of segment.parentNames) {
      if (isStairs(parentName)) {
        stairs.add(parentName);
      }
    }
  }

  // Если сегмент - коридор, собираем комнаты и лестницы из точек
  if (segment.type === "corridor") {
    for (const pt of activePoints) {
      if (isRoom(pt.parent)) {
        rooms.add(pt.parent);
      }
      // Лестницы в коридоре помечаем отдельно (будут подсвечиваться по-другому)
      if (isStairs(pt.parent)) {
        stairs.add(pt.parent);
      }
    }
  }

  return {
    type: "segment",
    floor: segment.floor,
    routeFloors,
    routeStairs,
    stairs,
    rooms,
    endpointRooms,
    activePoints,
    isCorridorSegment: segment.type === "corridor",
  };
}
